import { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  Home, CalendarCheck, Briefcase, Calculator, ScanLine, Wrench, GraduationCap,
  TrendingUp, Bell, LayoutDashboard, Users, Contact, DollarSign, Settings, SlidersHorizontal,
  LogOut, Waves, User, HelpCircle, ChevronDown, MessageSquare, Building2, FlaskConical, Clock, Rocket, UserCog, ShieldCheck,
} from "lucide-react";

const MODULES = {
  home: { label: "Home", icon: Home, path: "/" },
  today: { label: "Today", icon: CalendarCheck, path: "/today" },
  jobs: { label: "Jobs", icon: Briefcase, path: "/jobs" },
  estimator: { label: "Estimator", icon: Calculator, path: "/estimator" },
  shadow_scan: { label: "Shadow Scan", icon: ScanLine, path: "/shadow-scan" },
  equipment: { label: "Equipment", icon: Wrench, path: "/equipment" },
  timesheet: { label: "My Time & Pay", icon: Clock, path: "/timesheet" },
  academy: { label: "Academy", icon: GraduationCap, path: "/academy" },
  performance: { label: "Performance", icon: TrendingUp, path: "/performance" },
  alerts: { label: "Alerts", icon: Bell, path: "/alerts" },
  command_center: { label: "Command Center", icon: LayoutDashboard, path: "/command-center" },
  crews: { label: "Crews", icon: Users, path: "/crews" },
  customers: { label: "Customers", icon: Contact, path: "/customers" },
  financial_analytics: { label: "Financial", icon: DollarSign, path: "/financial" },
  square_readiness: { label: "Prod Readiness", icon: Rocket, path: "/square-readiness" },
  feedback: { label: "Field QA", icon: MessageSquare, path: "/feedback" },
  organization: { label: "Organization", icon: Building2, path: "/organization" },
  team: { label: "Team & Access", icon: UserCog, path: "/team" },
  settings: { label: "Settings", icon: Settings, path: "/settings" },
  security: { label: "Security", icon: ShieldCheck, path: "/security" },
  advanced_settings: { label: "Advanced Settings", icon: SlidersHorizontal, path: "/settings/advanced" },
};

const ROLE_LABEL = { owner: "Owner", manager: "Manager", technician: "Technician" };
const DOCK_KEYS = ["home", "today", "jobs", "estimator", "shadow_scan", "equipment"]; // eslint-disable-line no-unused-vars

export default function AppShell({ children }) {
  const { user, nav, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [fieldTest, setFieldTest] = useState(false);
  const menuRef = useRef(null);

  const doLogout = async () => { setMenuOpen(false); await logout(); navigate("/login"); };

  useEffect(() => {
    api.get("/settings/field-test").then(({ data }) => setFieldTest(!!data.field_test_mode)).catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    const h = () => api.get("/settings/field-test").then(({ data }) => setFieldTest(!!data.field_test_mode)).catch(() => {});
    window.addEventListener("field-test-changed", h);
    return () => window.removeEventListener("field-test-changed", h);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (path) => { setMenuOpen(false); navigate(path); };

  const NavItems = () => (
    <>
      {nav.map((key) => {
        const m = MODULES[key];
        if (!m) return null;
        const Icon = m.icon;
        return (
          <NavLink key={key} to={m.path} end={m.path === "/"} data-testid={`nav-${key}`}
            className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold transition-colors"
            style={({ isActive }) => ({ background: isActive ? "var(--accent)" : "transparent", color: isActive ? "#fff" : "var(--txt2)", minHeight: 50 })}>
            <Icon size={20} /> <span>{m.label}</span>
          </NavLink>
        );
      })}
    </>
  );

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg-app)" }}>
      <aside className="hidden md:flex flex-col w-60 shrink-0 border-r p-4 gap-1"
        style={{ borderColor: "var(--brd)", background: "var(--bg-surface)" }}>
        <button onClick={() => navigate("/")} data-testid="home-logo" aria-label="Home"
          className="flex items-center gap-2 px-2 py-3 mb-2 rounded-xl transition-colors hover:bg-[var(--bg-elev)] text-left w-full">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "var(--accent)" }}>
            <Waves size={22} color="#fff" />
          </div>
          <div>
            <div className="font-head text-lg font-extrabold leading-none">SHADOW GUIDE</div>
            <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--accent2)" }}>FreshSurf OS</div>
          </div>
        </button>
        <nav className="flex flex-col gap-1 overflow-y-auto flex-1"><NavItems /></nav>
        <button onClick={doLogout} data-testid="logout-btn" className="sg-btn sg-btn-ghost mt-2">
          <LogOut size={18} /> Sign Out
        </button>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="flex items-center justify-between px-4 sm:px-6 py-3 border-b sticky top-0 z-20"
          style={{ borderColor: "var(--brd)", background: "var(--bg-surface)" }}>
          <button onClick={() => navigate("/")} data-testid="home-logo-mobile" aria-label="Home" className="flex items-center gap-2 md:hidden">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "var(--accent)" }}>
              <Waves size={18} color="#fff" />
            </div>
            <span className="font-head text-lg font-extrabold">SHADOW GUIDE</span>
          </button>
          <div className="hidden md:block font-head text-sm uppercase tracking-widest" style={{ color: "var(--txt2)" }}>
            FreshSurf Crew Operating System
          </div>

          {/* Profile menu */}
          <div className="relative" ref={menuRef}>
            <button onClick={() => setMenuOpen((o) => !o)} data-testid="profile-menu-btn"
              className="flex items-center gap-3 rounded-full pl-1 pr-2 py-1 transition-colors"
              style={{ background: menuOpen ? "var(--bg-elev)" : "transparent" }}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center font-head font-bold"
                style={{ background: "var(--accent)", color: "#fff" }}>{user?.name?.[0]}</div>
              <div className="text-left hidden sm:block">
                <div className="text-sm font-semibold leading-tight">{user?.name}</div>
                <div className="text-[11px] uppercase tracking-wider" style={{ color: "var(--accent)" }}>{ROLE_LABEL[user?.role]}</div>
              </div>
              <ChevronDown size={16} style={{ color: "var(--txt2)" }} />
            </button>

            {menuOpen && (
              <div className="absolute right-0 mt-2 w-56 sg-card p-2 sg-fade z-50" data-testid="profile-menu" style={{ background: "var(--bg-surface)" }}>
                <div className="px-3 py-2 border-b mb-1" style={{ borderColor: "var(--brd)" }}>
                  <div className="font-semibold text-sm">{user?.name}</div>
                  <div className="text-xs" style={{ color: "var(--txt2)" }}>{user?.email}</div>
                </div>
                <MenuItem icon={User} label="My Profile" testid="menu-profile" onClick={() => go("/settings")} />
                <MenuItem icon={Settings} label="Settings" testid="menu-settings" onClick={() => go("/settings")} />
                <MenuItem icon={HelpCircle} label="Help" testid="menu-help" onClick={() => go("/settings")} />
                <div className="border-t my-1" style={{ borderColor: "var(--brd)" }} />
                <MenuItem icon={LogOut} label="Log Out" testid="menu-logout" danger onClick={doLogout} />
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 pb-24 md:pb-6 overflow-x-hidden">
          {fieldTest && (
            <div data-testid="field-test-banner" className="flex items-center justify-center gap-2 mb-4 px-4 py-2 rounded-xl text-sm font-bold"
              style={{ background: "rgba(245,158,11,0.15)", color: "#F59E0B", border: "1px solid #F59E0B" }}>
              <FlaskConical size={16} /> CONTROLLED FIELD TEST MODE — practice environment. Test jobs & payments are excluded from live revenue and metrics.
            </div>
          )}
          {children}
        </main>

        <nav className="md:hidden fixed bottom-0 left-0 right-0 flex overflow-x-auto border-t z-30"
          style={{ borderColor: "var(--brd)", background: "var(--bg-surface)" }}>
          {nav.map((key) => {
            const m = MODULES[key]; if (!m) return null; const Icon = m.icon;
            return (
              <NavLink key={key} to={m.path} end={m.path === "/"} data-testid={`dock-${key}`}
                className="flex-1 min-w-[74px] flex flex-col items-center gap-1 py-2 text-[10px] font-semibold text-center"
                style={({ isActive }) => ({ color: isActive ? "var(--accent)" : "var(--txt2)" })}>
                <Icon size={20} /> {m.label}
              </NavLink>
            );
          })}
        </nav>
      </div>
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, testid, danger }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors hover:bg-[var(--bg-elev)]"
      style={{ color: danger ? "var(--danger)" : "var(--txt)" }}>
      <Icon size={18} /> {label}
    </button>
  );
}
